import { Box, Checkbox, TablePagination } from "@mui/material";
import { ChangeEvent, useEffect } from "react";
import { useTranslation } from "react-i18next";
import DataTable, {
  DataTableColumn,
  DataTableProps,
} from "src/grading/components/DataTable/DataTable";

export const ROWS_PER_PAGE_OPTIONS = [25, 50, 100];

type RowId = string | number;

export interface PagedDataTableProps<T> extends DataTableProps<T> {
  /** Rows across every page (the server's `total`). */
  total: number;
  /** Zero-based page index. */
  page: number;
  rowsPerPage: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  /** Adds a checkbox column. Select-all applies to the current page only. */
  selectable?: boolean;
  selectedIds?: RowId[];
  onSelectionChange?: (ids: RowId[]) => void;
  /** Names the row in its checkbox label ("Select {name}"). Defaults to the row key. */
  getRowLabel?: (row: T) => string;
}

/** `DataTable` with server paging (25/50/100 rows) and optional row selection. */
export const PagedDataTable = <T,>({
  total,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  selectable = false,
  selectedIds = [],
  onSelectionChange,
  getRowLabel,
  columns,
  rows,
  getRowKey,
  loading,
  ...tableProps
}: PagedDataTableProps<T>) => {
  const { t } = useTranslation();

  // After rows disappear (a bulk deactivate, a narrower filter) the page can fall past the end.
  const lastPage = Math.max(0, Math.ceil(total / rowsPerPage) - 1);
  const shownPage = Math.min(page, lastPage);
  useEffect(() => {
    if (!loading && page > lastPage) onPageChange(lastPage);
  }, [loading, page, lastPage, onPageChange]);

  const selected = new Set<RowId>(selectedIds);
  const pageIds = rows.map(getRowKey);
  const selectedOnPage = pageIds.filter((id) => selected.has(id)).length;
  const allOnPage = pageIds.length > 0 && selectedOnPage === pageIds.length;

  const toggleAll = () => {
    if (allOnPage) {
      onSelectionChange?.(selectedIds.filter((id) => !pageIds.includes(id)));
    } else {
      onSelectionChange?.([
        ...selectedIds,
        ...pageIds.filter((id) => !selected.has(id)),
      ]);
    }
  };

  const toggleRow = (id: RowId) =>
    onSelectionChange?.(
      selected.has(id)
        ? selectedIds.filter((other) => other !== id)
        : [...selectedIds, id],
    );

  const selectionColumn: DataTableColumn<T> = {
    key: "__select",
    width: 48,
    header: (
      <Checkbox
        size="small"
        checked={allOnPage}
        indeterminate={selectedOnPage > 0 && !allOnPage}
        disabled={loading || pageIds.length === 0}
        onChange={toggleAll}
        inputProps={{ "aria-label": t("table.selectAllOnPage") }}
      />
    ),
    render: (row) => {
      const id = getRowKey(row);
      return (
        <Checkbox
          size="small"
          checked={selected.has(id)}
          onChange={() => toggleRow(id)}
          inputProps={{
            "aria-label": t("table.selectRow", {
              name: getRowLabel ? getRowLabel(row) : String(id),
            }),
          }}
        />
      );
    },
  };

  return (
    <Box>
      <DataTable<T>
        {...tableProps}
        columns={selectable ? [selectionColumn, ...columns] : columns}
        rows={rows}
        getRowKey={getRowKey}
        loading={loading}
        aria-rowcount={total + 1}
        rowIndexOffset={shownPage * rowsPerPage}
      />
      {total > 0 ? (
        <TablePagination
          component="div"
          count={total}
          page={shownPage}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          onPageChange={(_event, next) => onPageChange(next)}
          onRowsPerPageChange={(
            event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
          ) => onRowsPerPageChange(parseInt(event.target.value, 10))}
          labelRowsPerPage={t("table.rowsPerPage")}
          labelDisplayedRows={({ from, to, count }) =>
            t("table.displayedRows", { from, to, count })
          }
          getItemAriaLabel={(type) =>
            t(
              {
                first: "table.firstPage",
                last: "table.lastPage",
                next: "table.nextPage",
                previous: "table.previousPage",
              }[type],
            )
          }
        />
      ) : null}
    </Box>
  );
};

export default PagedDataTable;
